import { Checkbox, Input, DatePicker } from "@legal-500/legal500-design-system";
import { useEffect, useState } from "react";
import type { FieldErrors, UseFormRegister, UseFormWatch, Control } from "react-hook-form";
import { Controller } from "react-hook-form";
import type { FormFieldConfig, HierarchicalItem } from "../../types/uiTypes";
import { SearchableSelect } from "./SearchableSelect/SearchableSelect";
import styles from "./forms/Form.module.css";

type FormFieldProps<T extends HierarchicalItem> = {
  field: FormFieldConfig;
  register: UseFormRegister<Record<string, unknown>>;
  errors: FieldErrors<Record<string, unknown>>;
  watch: UseFormWatch<Record<string, unknown>>;
  control: Control<Record<string, unknown>>;
  mode: "create" | "edit";
  parentItem?: T;
  itemOptions: Array<{ item: T; level: number }>;
  parentOptionLabel?: (item: T) => string;
  disabled?: boolean;
};

export function FormField<T extends HierarchicalItem>({
  field,
  register,
  errors,
  watch,
  control,
  mode,
  parentItem,
  itemOptions,
  parentOptionLabel,
  disabled = false,
}: FormFieldProps<T>) {
  if (field.hidden) return null;

  const error = errors[field.name]?.message;
  const errorMessage = typeof error === "string" ? error : undefined;

  // Load async options for select fields
  const [selectOptions, setSelectOptions] = useState<
    Array<{ value: string | number | null; label: string }>
  >([]);
  const [loadingOptions, setLoadingOptions] = useState(false);

  useEffect(() => {
    if (field.type === "select" && typeof field.options === "function") {
      setLoadingOptions(true);
      field.options().then(options => {
        setSelectOptions(options);
        setLoadingOptions(false);
      });
    } else if (field.type === "select" && Array.isArray(field.options)) {
      setSelectOptions(field.options);
    }
  }, [field.options, field.type]);

  // Get current value for selects with async options
  const currentValue = watch(field.name);

  switch (field.type) {
    case "checkbox": {
      const value = watch(field.name);
      return (
        <div key={field.name} className={styles.formGroup}>
          <Checkbox
            {...register(field.name)}
            checked={!!value}
            label={field.label}
            disabled={disabled}
          />
        </div>
      );
    }

    case "select": {
      // Use custom SearchableSelect for searchable fields
      if (field.searchable) {
        // For parentId, use hierarchy options (keep IDs as numbers)
        const options =
          field.name === "parentId"
            ? [
                { value: null, label: "No Parent (Top Level)" },
                ...itemOptions.map(({ item: option, level }) => ({
                  value: option.id,
                  label: `${"  ".repeat(level)}${parentOptionLabel?.(option) ?? option.name}`,
                })),
              ]
            : selectOptions;

        return (
          <Controller
            key={field.name}
            name={field.name}
            control={control}
            render={({ field: { onChange, value } }) => (
              <SearchableSelect
                label={field.label}
                name={field.name}
                value={value as string | number | null}
                options={options}
                placeholder={field.placeholder}
                required={field.required}
                helperText={field.helperText}
                error={errorMessage}
                disabled={
                  disabled || (field.name === "parentId" && mode === "create" && !!parentItem)
                }
                onChange={onChange}
              />
            )}
          />
        );
      }

      // Regular select for non-searchable fields
      return (
        <div key={field.name} className={styles.formGroup}>
          <label className={`${styles.formLabel} ${field.required ? styles.required : ""}`}>
            {field.label}
          </label>
          <select
            {...register(field.name, {
              valueAsNumber: field.valueAsNumber || false,
            })}
            className={styles.formSelect}
            disabled={disabled || loadingOptions}
            value={currentValue as string | number}
          >
            {loadingOptions && <option value="">Loading...</option>}
            {!loadingOptions && field.placeholder && <option value="">{field.placeholder}</option>}
            {!loadingOptions &&
              selectOptions.map(opt => (
                <option key={opt.value ?? ""} value={opt.value ?? ""}>
                  {opt.label}
                </option>
              ))}
          </select>
          {errorMessage && <span className={styles.fieldError}>{errorMessage}</span>}
        </div>
      );
    }

    case "textarea":
      return (
        <div key={field.name} className={styles.formGroup}>
          <label className={`${styles.formLabel} ${field.required ? styles.required : ""}`}>
            {field.label}
          </label>
          <textarea
            {...register(field.name)}
            className={styles.formTextarea}
            placeholder={field.placeholder}
            rows={4}
            disabled={disabled}
          />
          {field.helperText && !errorMessage && (
            <span className={styles.helperText}>{field.helperText}</span>
          )}
          {errorMessage && <span className={styles.fieldError}>{errorMessage}</span>}
        </div>
      );

    case "date": {
      return (
        <div key={field.name} className={styles.formGroup}>
          <Controller
            name={field.name}
            control={control}
            render={({ field: { onChange, value, ...fieldProps } }) => (
              <DatePicker
                {...fieldProps}
                value={(value ?? "") as string | undefined}
                onChange={onChange}
                label={field.label}
                error={errorMessage}
                required={field.required}
                fullWidth
                placeholder={field.placeholder}
                helperText={!errorMessage ? field.helperText : undefined}
                disabled={disabled}
              />
            )}
          />
        </div>
      );
    }

    default:
      return (
        <div key={field.name} className={styles.formGroup}>
          <label className={`${styles.formLabel} ${field.required ? styles.required : ""}`}>
            {field.label}
          </label>
          <Input
            type={field.type}
            {...register(field.name, {
              valueAsNumber: field.type === "number",
            })}
            error={errorMessage}
            aria-invalid={!!errorMessage}
            required={field.required}
            fullWidth
            placeholder={field.placeholder}
            helperText={!errorMessage ? field.helperText : undefined}
            disabled={disabled}
          />
        </div>
      );
  }
}
